import React from "react";
import { CheckCircle2, XCircle } from "lucide-react";

const qualifyItems = [
  "Be an active customer with a current account",
  "Have at least one active service with us",
  "Remain in good standing",
  "Use your approved affiliate link when referring new customers",
  "Follow all referral and promotional guidelines",
];

const importantRules = [
  "Affiliate commissions are only available to active customers.",
  "Self-referrals are not allowed. You cannot use your own affiliate link to earn commission on your own purchases, accounts, renewals, or related accounts.",
  "Spam, misleading advertising, fake claims, or unauthorized promotions are not allowed.",
  "Affiliate commissions may be removed if the referred customer cancels, receives a refund, files a payment dispute, or violates our terms of service.",
  "We reserve the right to review, approve, deny, or cancel affiliate commissions if abuse, fraud, or policy violations are found.",
];

const AffiliateProgram = () => {
  return (
    <div className="py-16 bg-white">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <h1 className="font-heading font-extrabold text-4xl text-p3-navy mb-3">Affiliate Program</h1>
        <p className="font-heading font-semibold text-lg text-p3-blue mb-8">
          Earn Money by Referring New Customers
        </p>

        <p className="text-sm text-p3-muted leading-relaxed mb-4">
          Our affiliate program gives active customers the opportunity to earn commission by referring new
          clients to our services. When someone signs up through your affiliate link and becomes a paying
          customer, you can earn a percentage of their payment as a referral commission.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          This program is designed for real customers who use and understand our service. That means you must
          have an active customer account before you can participate as an affiliate.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Who Can Become an Affiliate?</h2>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">To qualify for the affiliate program, you must:</p>
        <ul className="space-y-2 mb-6">
          {qualifyItems.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-p3-muted">
              <CheckCircle2 size={16} className="text-p3-blue mt-0.5 shrink-0" /> {item}
            </li>
          ))}
        </ul>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          Affiliate accounts are not available to people who have not purchased or maintained service with us.
          We want referrals to come from customers who can honestly speak about the value of what we provide.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Commission Structure</h2>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">
          Affiliates earn <span className="font-semibold text-p3-navy">20%</span> of each qualifying payment
          made by a customer they refer.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          For example, when someone signs up using your affiliate link and their payment is approved, your
          affiliate account will be credited with a 20% commission based on the eligible payment amount.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Commission Approval Delay</h2>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">
          Affiliate commissions are delayed for <span className="font-semibold text-p3-navy">30 days</span>{" "}
          before becoming eligible for payout.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">
          This delay helps confirm that the referred customer&apos;s account is active, valid, and in good
          standing. Commissions are only paid if the referred customer&apos;s account remains active after the
          commission delay period.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          If the referred account is canceled, refunded, disputed, charged back, or found to be invalid during
          the 30-day review period, the commission may be declined or removed.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Minimum Payout Amount</h2>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">
          Affiliates must reach a minimum balance of <span className="font-semibold text-p3-navy">$100.00</span>{" "}
          before requesting a withdrawal.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          Once your approved affiliate balance reaches $100 or more, you may submit a payout request through
          the support department.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Payout Requests</h2>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">
          All affiliate payout requests must be submitted through the Support Department.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          When submitting a request, include your account name, affiliate account information, and preferred
          payout details if required. Payouts are reviewed before processing to confirm that all commissions
          are valid and eligible.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Affiliate Links</h2>
        <p className="text-sm text-p3-muted leading-relaxed mb-3">
          Each approved affiliate receives a custom referral link. This link is used to track customers who
          sign up through your recommendation.
        </p>
        <p className="text-sm text-p3-muted leading-relaxed mb-10">
          Your affiliate link must be used for referrals to be tracked properly. If someone signs up without
          using your affiliate link, we may not be able to manually apply the referral later.
        </p>

        <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Important Rules</h2>
        <ul className="space-y-2 mb-10">
          {importantRules.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-p3-muted">
              <XCircle size={16} className="text-red-400 mt-0.5 shrink-0" /> {item}
            </li>
          ))}
        </ul>

        <div className="bg-p3-cream rounded-2xl p-6">
          <h2 className="font-heading font-bold text-xl text-p3-navy mb-3">Why Join?</h2>
          <p className="text-sm text-p3-muted leading-relaxed mb-3">
            If you already use our service and know someone who could benefit from it, the affiliate program
            gives you a simple way to earn by sharing it. Send people your link, help them get started, and
            earn commission when they become paying customers.
          </p>
          <p className="text-sm font-semibold text-p3-navy">
            Good customers bring good customers. That is how real growth happens.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateProgram;
